import { CommonModule, DatePipe } from '@angular/common';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { Title } from '@angular/platform-browser';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  TemplateRef,
  Type,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { OdysseyOffcanvas, OffcanvasSize } from '@digiwall/core';
import { combineLatest, EMPTY, forkJoin, merge, Observable } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import {
  KanbanBoardComponent,
  KanbanColumn,
  KanbanCreateGroupEvent,
  KanbanDropTarget,
  KanbanGroup,
  KanbanGroupDropEvent,
  KanbanGroupHeaderTemplateDirective,
  KanbanBacklogFooterTemplateDirective,
  KanbanBacklogListFooterTemplateDirective,
  KanbanItem,
  KanbanItemDropEvent,
  KanbanItemStatusChangeEvent,
  KanbanItemTemplateDirective,
  KanbanMenuItem,
  ToggleSelect,
  KanbanRangeSelectComponent,
  KanbanToolbarComponent,
  KanbanWeekNavComponent,
} from '@digiwall/goliath-core';
import {
  PlanningConfigurationDto,
  PlanningConfigurationService,
  PlanningDataSource,
  PlanningGroupDto,
  PlanningGrouping,
  PlanningItemDto,
  PlanningPeriod,
} from '@digiwall/goliath-documents/proxy';
import { GoliathDocumentsEnumerationTypeStore } from '../enumeration-types/enumeration-type.store';
import { BACKLOG_PAGE_SIZE } from './adapters/base-planning.adapter';
import { PlanningDataSourceAdapter, PlanningPeriodQuery } from './adapters/planning-adapter';
import { PLANNING_ADAPTERS } from './adapters/planning-adapter.registry';
import { WORK_ORDER_NOT_PLANNED_STATUS_ID } from '../work-order/work-order-status-rules';
import {
  PlanningCardConfigPanelComponent,
  PlanningCardConfigSource,
} from './card-config/planning-card-config-panel.component';
import { PlanningCardFieldVisibilityStore } from './card-config/planning-card-field-visibility.store';
import { PlanningFreeGroupCardComponent } from './planning-free-group-card.component';
import { PlanningGroupDialogComponent } from './planning-group-dialog/planning-group-dialog.component';
import { PlanningFilterHostDirective } from './planning-filter-host.directive';
import {
  EMPTY_FILTER_SETTINGS,
  FilterSettingsMap,
  PlanningBoardFilterState,
} from '../planning-common/filters';
import {
  PlanningGroupData,
  PlanningItemData,
  bucketIdForDate,
  compareRequestedDate,
  dataSourceOfKanbanId,
  enumAccentColor,
  groupKanbanId,
  requestedDateAtOrBefore,
  statusColor,
  toKanbanGroup,
  toKanbanItem,
} from './planning-board.mapping';
import {
  addDays,
  buildPlanningBuckets,
  isWeekendBucket,
  planningWindow,
  PlanningScaleBucket,
  shiftAnchor,
  toIsoDay,
} from './planning-scale';
import { PlanningViewStateStore } from './planning-view-state.store';

/** A configuration data source with a resolved (implemented) adapter. */
interface ResolvedSource {
  dataSource: PlanningDataSource;
  adapter: PlanningDataSourceAdapter;
}

/** Loaded board slice for one data source (planned items in view, backlog, groups in view). */
interface SourceBoard {
  planned: PlanningItemDto[];
  backlog: PlanningItemDto[];
  /** Total unplanned items server-side (backlog is capped at the current page size — see load more). */
  backlogTotalCount: number;
  groups: PlanningGroupDto[];
}

const EMPTY_BOARD: SourceBoard = { planned: [], backlog: [], backlogTotalCount: 0, groups: [] };

/** Stable empty reference for a source with no loaded items (avoids new arrays each CD pass). */
const EMPTY_ITEMS: PlanningItemDto[] = [];

/**
 * Debounce window (ms) for navigation-driven board reloads. Rapid prev/next/range/today spam collapses
 * into a single load, and the switchMap below cancels the previous window's in-flight requests.
 */
const NAVIGATION_DEBOUNCE_MS = 250;

/**
 * Generic planning board (route `planning/:configId`). Loads a PlanningConfiguration, resolves an
 * adapter per configured data source, and drives one kanban board: columns come from the scale engine,
 * items/groups come from each adapter. Drag intents are applied optimistically and the affected data
 * source is reloaded on failure (the global HTTP error handler surfaces the reason). Data sources with
 * no adapter yet (Delivery/Reception/Production) show a "not yet available" note.
 */
@Component({
  selector: 'document-planning-board',
  templateUrl: './planning-board.component.html',
  styleUrl: './planning-board.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe, PlanningCardFieldVisibilityStore, PlanningViewStateStore],
  imports: [
    CommonModule,
    LocalizationModule,
    KanbanBoardComponent,
    KanbanToolbarComponent,
    KanbanWeekNavComponent,
    KanbanRangeSelectComponent,
    KanbanItemTemplateDirective,
    KanbanGroupHeaderTemplateDirective,
    KanbanBacklogFooterTemplateDirective,
    KanbanBacklogListFooterTemplateDirective,
    PlanningFreeGroupCardComponent,
    PlanningFilterHostDirective,
    PlanningCardConfigPanelComponent,
  ],
})
export class PlanningBoardComponent {
  private readonly service = inject(PlanningConfigurationService);
  private readonly datePipe = inject(DatePipe);
  private readonly localization = inject(LocalizationService);
  private readonly injector = inject(Injector);
  private readonly store = inject(GoliathDocumentsEnumerationTypeStore);
  private readonly modalService = inject(NgbModal);
  private readonly confirmation = inject(ConfirmationService);
  private readonly title = inject(Title);
  private readonly offcanvas = inject(OdysseyOffcanvas);
  private readonly visibilityStore = inject(PlanningCardFieldVisibilityStore);
  private readonly viewStore = inject(PlanningViewStateStore);

  readonly configId = input.required<string>();

  protected readonly config = signal<PlanningConfigurationDto | undefined>(undefined);
  protected readonly anchor = signal<Date>(new Date());
  protected readonly range = signal<PlanningPeriod | undefined>(undefined);
  protected readonly search = signal('');
  /** Whether weekend day columns are shown (daily granularity only). */
  protected readonly showWeekends = signal(true);
  /** Fit all columns into the viewport (no horizontal scroll) regardless of zoom. Default off. */
  protected readonly fitToScreen = signal(false);
  protected readonly legendCollapsed = signal(false);

  protected readonly planningTitle = computed(
    () => this.config()?.name?.current || this.config()?.code || '',
  );

  private readonly resolvedSources = signal<ResolvedSource[]>([]);
  private readonly unavailable = signal<PlanningDataSource[]>([]);
  private readonly groupingByDs = signal<Record<string, PlanningGrouping>>({});
  private readonly boardData = signal<Record<string, SourceBoard>>({});
  /**
   * Current backlog page size, shared by every source ("load more" bumps it). The backlog is
   * window-independent, so this persists across navigation and is reset only when the board (config)
   * changes. A change re-triggers the load pipeline (see the constructor's combineLatest).
   */
  private readonly backlogLimit = signal(BACKLOG_PAGE_SIZE);

  /** Latest client-side filter predicate per data source (pass-all when absent). */
  private readonly filterPredicates = signal<Record<string, (dto: PlanningItemDto) => boolean>>({});

  /** Restored filter selections per data source (set once on load), fed into the filter controls. */
  private readonly initialFilterState = signal<Record<string, PlanningBoardFilterState>>({});
  /** Live filter selections per data source (updated as the user edits them), persisted. */
  private readonly filterStateByDs = signal<Record<string, PlanningBoardFilterState>>({});

  // Coarseness rank used to order/choose zoom ranges.
  private readonly rank: Record<string, number> = {
    [PlanningPeriod.Day]: 1,
    [PlanningPeriod.Week]: 2,
    [PlanningPeriod.Month]: 3,
    [PlanningPeriod.Quarter]: 4,
    [PlanningPeriod.Semester]: 5,
    [PlanningPeriod.Year]: 6,
  };

  constructor() {
    // Ensure enumeration types are available (legend, card colours, status labels).
    this.store.load();
    // Load the routed configuration, then resolve its data-source adapters.
    toObservable(this.configId)
      .pipe(
        switchMap(id => this.service.get(id)),
        takeUntilDestroyed(),
      )
      .subscribe(config => this.onConfigLoaded(config));

    // Reload every resolved source whenever the visible window (anchor/range) changes. Debounced so a
    // burst of prev/next/range/today clicks coalesces into one load, and switchMapped so a newer window
    // cancels the previous window's in-flight board/backlog requests — otherwise a slow earlier response
    // could land after a newer one and leave the board showing a stale window.
    // A backlogLimit change ("load more") re-triggers the same load with the larger page; because the
    // sources already have a board entry, loading() stays false and the backlog grows in place.
    combineLatest([toObservable(this.query), toObservable(this.backlogLimit)])
      .pipe(
        debounceTime(NAVIGATION_DEBOUNCE_MS),
        switchMap(([query]) => {
          const sources = this.resolvedSources();
          if (!query || sources.length === 0) {
            return EMPTY;
          }
          return merge(...sources.map(source => this.loadSource(source.dataSource, query)));
        }),
        takeUntilDestroyed(),
      )
      .subscribe(({ dataSource, board }) => this.patchSource(dataSource, () => board));

    // Persist view state (zoom, displayed date, quick-search, weekend toggle) whenever it changes,
    // once the routed config has loaded. Restored in onConfigLoaded so the board reopens where the
    // user left it. Guarded on the loaded config matching the route to skip the transient window
    // after a configId change but before its config lands.
    effect(() => {
      const config = this.config();
      const range = this.range();
      const anchor = this.anchor();
      const search = this.search();
      const showWeekends = this.showWeekends();
      const fitToScreen = this.fitToScreen();
      const filters = this.filterStateByDs();
      if (!config || config.id !== this.configId()) {
        return;
      }
      this.viewStore.save({
        range,
        anchor: toIsoDay(anchor),
        search,
        showWeekends,
        fitToScreen,
        filters,
      });
    });
  }

  // ── Scale / navigation ─────────────────────────────────────────────────────

  protected readonly ranges = computed<PlanningPeriod[]>(() =>
    [...(this.config()?.ranges ?? [])].sort((a, b) => (this.rank[a] ?? 0) - (this.rank[b] ?? 0)),
  );

  protected readonly rangeOptions = computed<ToggleSelect<PlanningPeriod>[]>(() =>
    this.ranges().map(period => ({ value: period, label: this.periodLabel(period) })),
  );

  protected readonly buckets = computed<PlanningScaleBucket[]>(() => {
    const config = this.config();
    const range = this.range();
    const anchor = this.anchor();
    if (!config?.granularity) {
      return [];
    }
    const granularity = config.granularity;
    if (!range) {
      return [];
    }
    const all = buildPlanningBuckets(granularity, range, anchor);
    // Drop weekend columns (and any items planned on them) when the user has hidden them.
    return this.showWeekends() ? all : all.filter(bucket => !isWeekendBucket(granularity, bucket));
  });

  protected readonly columns = computed<KanbanColumn<PlanningScaleBucket>[]>(() => {
    const config = this.config();
    const buckets = this.buckets();
    if (!config?.granularity) {
      return [];
    }
    const granularity = config.granularity;
    const todayIso = toIsoDay(new Date());
    return buckets.map(bucket => ({
      id: bucket.id,
      title: this.bucketTitle(granularity, bucket),
      subtitle: this.bucketSubtitle(granularity, bucket),
      highlighted: todayIso >= toIsoDay(bucket.start) && todayIso < toIsoDay(bucket.end),
      highlightLabel: this.localization.instant('Doc::Planning:Today'),
      weekend: isWeekendBucket(granularity, bucket),
      // Disable elapsed periods: entirely past when the bucket's exclusive end is at/before today.
      disabled: toIsoDay(bucket.end) <= todayIso,
      data: bucket,
    }));
  });

  /** The weekend show/hide toggle only makes sense when columns are single days. */
  protected readonly weekendToggleVisible = computed<boolean>(
    () => this.config()?.granularity === PlanningPeriod.Day,
  );

  protected readonly rangeLabel = computed(() => {
    const range = this.range();
    const anchor = this.anchor();
    if (!range) {
      return '';
    }
    const window = planningWindow(range, anchor);
    if (range === PlanningPeriod.Month) {
      return this.datePipe.transform(window.start, 'MMMM yyyy') ?? '';
    }
    const lastDay = addDays(window.end, -1);
    return `${this.datePipe.transform(window.start, 'd MMM')} - ${this.datePipe.transform(lastDay, 'd MMM')}`;
  });

  /** True when today falls inside the visible window — used to disable the "today" jump. */
  protected readonly todayInView = computed<boolean>(() => {
    const range = this.range();
    if (!range) {
      return false;
    }
    const window = planningWindow(range, this.anchor());
    const todayIso = toIsoDay(new Date());
    return todayIso >= toIsoDay(window.start) && todayIso < toIsoDay(window.end);
  });

  protected readonly unavailableLabels = computed<string[]>(() => {
    const entities = this.store.planningDataSources();
    return this.unavailable().map(
      dataSource => entities.find(entity => entity.id === dataSource)?.name?.current ?? dataSource,
    );
  });

  protected previous(): void {
    const range = this.range();
    if (range) {
      this.anchor.update(anchor => shiftAnchor(range, anchor, -1));
    }
  }

  protected next(): void {
    const range = this.range();
    if (range) {
      this.anchor.update(anchor => shiftAnchor(range, anchor, 1));
    }
  }

  protected today(): void {
    this.anchor.set(new Date());
  }

  private periodLabel(period: PlanningPeriod): string {
    return (
      this.store.planningPeriods().find(entity => entity.id === period)?.name?.current ?? period
    );
  }

  // ── Board model (columns ← scale, items/groups ← adapters) ──────────────────

  /**
   * True until the board is ready to show: the config is still loading, or at least one resolved
   * data source has not delivered its first slice yet. Reloads on window navigation don't retrigger
   * it — those sources already have a (stale) board entry, which we keep until the new data lands.
   */
  protected readonly loading = computed<boolean>(() => {
    const config = this.config();
    const sources = this.resolvedSources();
    const data = this.boardData();
    if (!config) {
      return true;
    }
    if (sources.length === 0) {
      return false;
    }
    return sources.some(source => !(source.dataSource in data));
  });

  private readonly sourceMap = computed(
    () => new Map(this.resolvedSources().map(source => [source.dataSource, source])),
  );

  protected readonly kanbanItems = computed<KanbanItem<PlanningItemData>[]>(() => {
    const buckets = this.buckets();
    const data = this.boardData();
    const predicates = this.filterPredicates();
    const todayIso = toIsoDay(new Date());
    const items: KanbanItem<PlanningItemData>[] = [];
    // Backlog items from every source share the single "À planifier" column, so they are collected
    // across sources and sorted together by desired date before being emitted (see below).
    const backlog: { dataSource: PlanningDataSource; dto: PlanningItemDto; color?: string }[] = [];
    for (const source of this.resolvedSources()) {
      const dataSource = source.dataSource;
      const board = data[dataSource];
      if (!board) {
        continue;
      }
      const statuses = source.adapter.statusEnumeration?.() ?? [];
      // Client-side filter predicate for this source (pass-all when none set). Removes items;
      // the separate quick-search only dims. Applied to both columns and backlog.
      const matches = predicates[dataSource] ?? (() => true);
      // Within a column/group the board renders items in input order, so emit them by placement order.
      const planned = [...board.planned].filter(matches).sort((a, b) => a.order - b.order);
      for (const dto of planned) {
        const columnId = dto.plannedDate
          ? bucketIdForDate(buckets, new Date(dto.plannedDate))
          : null;
        if (!columnId) {
          continue; // planned outside the visible window
        }
        // Planned: critical when the desired date is at/before the column's period start (its id).
        const requestedDateCritical = requestedDateAtOrBefore(dto.requestedDate, columnId);
        items.push(
          toKanbanItem(
            { dataSource, dto, requestedDateCritical },
            columnId,
            statusColor(statuses, dto.statusId),
          ),
        );
      }
      for (const dto of board.backlog.filter(matches)) {
        backlog.push({ dataSource, dto, color: statusColor(statuses, dto.statusId) });
      }
    }
    // Sort the "À planifier" column by desired date ascending (items without one go last), then emit.
    backlog.sort((a, b) => compareRequestedDate(a.dto.requestedDate, b.dto.requestedDate));
    for (const { dataSource, dto, color } of backlog) {
      // Backlog: critical when the desired date is at/before today.
      const requestedDateCritical = requestedDateAtOrBefore(dto.requestedDate, todayIso);
      items.push(toKanbanItem({ dataSource, dto, requestedDateCritical }, null, color));
    }
    return items;
  });

  protected readonly kanbanGroups = computed<KanbanGroup<PlanningGroupData>[]>(() => {
    const buckets = this.buckets();
    const data = this.boardData();
    const groups: KanbanGroup<PlanningGroupData>[] = [];
    for (const source of this.resolvedSources()) {
      const dataSource = source.dataSource;
      // Emit by placement order — groups share the column's top-level sequence with ungrouped items.
      const ordered = [...(data[dataSource]?.groups ?? [])].sort((a, b) => a.order - b.order);
      for (const dto of ordered) {
        const columnId = dto.date ? bucketIdForDate(buckets, new Date(dto.date)) : null;
        if (columnId) {
          groups.push(toKanbanGroup({ dataSource, dto }, columnId));
        }
      }
    }
    return groups;
  });

  protected readonly allowDirectItems = computed(() => {
    const grouping = this.groupingByDs();
    return this.resolvedSources().some(
      source => grouping[source.dataSource] !== PlanningGrouping.Required,
    );
  });

  protected readonly showNewGroupZone = computed(() => {
    const grouping = this.groupingByDs();
    return this.resolvedSources().some(
      source => grouping[source.dataSource] !== PlanningGrouping.Denied,
    );
  });

  protected readonly legendGroups = computed<
    { dataSourceLabel: string; entries: { label: string; color: string }[] }[]
  >(() => {
    const dsEntities = this.store.planningDataSources();
    const groups: { dataSourceLabel: string; entries: { label: string; color: string }[] }[] = [];
    for (const source of this.resolvedSources()) {
      const statuses = source.adapter.statusEnumeration?.() ?? [];
      const entries = statuses
        .map(s => ({ label: s.name?.current ?? '', color: enumAccentColor(s) || '#6c757d' }))
        .filter(e => e.label);
      if (entries.length) {
        const dataSourceLabel =
          dsEntities.find(e => e.id === source.dataSource)?.name?.current ?? source.dataSource;
        groups.push({ dataSourceLabel, entries });
      }
    }
    return groups;
  });

  protected readonly dimItem = computed<((item: KanbanItem) => boolean) | undefined>(() => {
    const text = this.search().trim().toLowerCase();
    if (!text) {
      return undefined;
    }
    return item => {
      const data = item.data as PlanningItemData;
      const haystack = this.sourceMap().get(data.dataSource)?.adapter.searchText(data.dto) ?? '';
      return !haystack.toLowerCase().includes(text);
    };
  });

  protected cardComponentFor(dataSource: PlanningDataSource): Type<unknown> | null {
    return this.sourceMap().get(dataSource)?.adapter.cardComponent ?? null;
  }

  protected cardInputsFor(data: PlanningItemData): Record<string, unknown> {
    return { dto: data.dto, requestedDateCritical: data.requestedDateCritical ?? false };
  }

  /** True when any source has more backlog items server-side than are currently loaded (drives "load more"). */
  protected readonly hasMoreBacklog = computed(() => {
    const data = this.boardData();
    return this.resolvedSources().some(source => {
      const board = data[source.dataSource];
      return !!board && board.backlogTotalCount > board.backlog.length;
    });
  });

  /** Grows the backlog page for every source by one page; the load pipeline reloads with the new size. */
  protected loadMore(): void {
    this.backlogLimit.update(limit => limit + BACKLOG_PAGE_SIZE);
  }

  // ── Drop rules ──────────────────────────────────────────────────────────────

  protected readonly canDrop = (item: KanbanItem, target: KanbanDropTarget): boolean => {
    const itemDs = (item.data as PlanningItemData).dataSource;
    const grouping = this.groupingByDs()[itemDs];
    if (target.kind === 'group') {
      if (dataSourceOfKanbanId(target.groupId) !== itemDs) {
        return false; // items never join a group of another data source
      }
      if (grouping === PlanningGrouping.Denied) {
        return false;
      }
    } else if (target.kind === 'column' && grouping === PlanningGrouping.Required) {
      return false; // this source must be planned inside a group
    } else if (target.kind === 'new-group' && grouping === PlanningGrouping.Denied) {
      return false;
    }
    return this.sourceMap().get(itemDs)?.adapter.canDrop?.(item, target) ?? true;
  };

  // ── Kanban event relay ───────────────────────────────────────────────────────

  protected onItemDrop(event: KanbanItemDropEvent): void {
    const data = event.item.data as PlanningItemData;
    const target = event.target;
    if (target.kind === 'new-group') {
      return; // handled through the createGroup event
    }
    if (target.kind === 'backlog') {
      this.unplanItem(data);
      return;
    }
    const bucket = this.bucketById(target.columnId);
    if (!bucket) {
      return;
    }
    const group = target.kind === 'group' ? this.groupByKanbanId(target.groupId) : null;
    this.planItem(data, bucket, group, this.dropOrder(event.source, target));
  }

  /**
   * Insertion order to persist for an item drop. The kanban's target index counts the dragged card
   * while it is still rendered in its source scope, so a downward move within the same scope is
   * off by one against the sibling list the server re-sequences.
   */
  private dropOrder(source: KanbanItemDropEvent['source'], target: KanbanDropTarget): number {
    if (target.kind !== 'column' && target.kind !== 'group') {
      return 0;
    }
    const sameScope =
      target.kind === 'group'
        ? source.groupId === target.groupId
        : source.groupId == null && source.columnId === target.columnId;
    return sameScope && source.index < target.index ? target.index - 1 : target.index;
  }

  protected onGroupDrop(event: KanbanGroupDropEvent): void {
    const data = event.group.data as PlanningGroupData;
    const bucket = this.bucketById(event.target.columnId);
    if (bucket) {
      this.moveGroup(data, bucket, this.groupDropOrder(event.source, event.target));
    }
  }

  /**
   * Insertion order to persist for a group drop. Like {@link dropOrder}, the kanban's target index
   * counts the dragged group while it is still rendered in its source column, so a downward move
   * within the same column is off by one against the sibling sequence the server re-sequences.
   */
  private groupDropOrder(
    source: KanbanGroupDropEvent['source'],
    target: KanbanGroupDropEvent['target'],
  ): number {
    return source.columnId === target.columnId && source.index < target.index
      ? target.index - 1
      : target.index;
  }

  /** A column's shared top-level sequence (group cards + ungrouped items), minus the moved entity. */
  private topLevelSlots(
    board: SourceBoard,
    columnId: string,
    movedId: string | undefined,
  ): { order: number }[] {
    const buckets = this.buckets();
    const inColumn = (value?: string): boolean =>
      !!value && bucketIdForDate(buckets, new Date(value)) === columnId;
    return [
      ...board.groups.filter(group => inColumn(group.date) && group.id !== movedId),
      ...board.planned.filter(
        item => inColumn(item.plannedDate) && !item.groupId && item.id !== movedId,
      ),
    ];
  }

  /**
   * Fractional order that renders a dropped entity in its final slot immediately: just before
   * whatever currently occupies `index` (or after the last sibling when appending). Uses the
   * neighbour's real order rather than `index`, because sibling orders are no longer contiguous
   * once the moved entity is pulled out of the sequence. The reload reconciles back to integers.
   */
  private optimisticSlotOrder(siblings: { order: number }[], index: number): number {
    const orders = siblings.map(sibling => sibling.order).sort((a, b) => a - b);
    if (orders.length === 0) {
      return 0;
    }
    if (index >= orders.length) {
      return orders[orders.length - 1] + 0.5;
    }
    return orders[Math.max(0, index)] - 0.5;
  }

  protected onGroupRename(data: PlanningGroupData, label: string): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    this.patchSource(ds, board => ({
      ...board,
      groups: board.groups.map(group => (group.id === data.dto.id ? { ...group, label } : group)),
    }));
    adapter.updateGroup({ configId: this.configId(), id: data.dto.id!, label }).subscribeOnce(
      updated => this.replaceGroup(ds, updated),
      () => this.reloadSource(ds),
    );
  }

  protected onGroupDelete(group: KanbanGroup): void {
    this.deleteGroup(group.data as PlanningGroupData);
  }

  protected onItemOpen(item: KanbanItem): void {
    const data = item.data as PlanningItemData;
    this.sourceMap().get(data.dataSource)?.adapter.openItem?.(data.dto);
  }

  protected onItemStatusChange(event: KanbanItemStatusChangeEvent): void {
    const data = event.item.data as PlanningItemData;
    // Picking the "unplanned" status isn't a status change — it sends the item back to the backlog
    // (same as the right-click Unplan), behind a confirmation.
    if (event.statusId === WORK_ORDER_NOT_PLANNED_STATUS_ID) {
      this.confirmUnplan(data);
      return;
    }
    this.changeItemStatus(data, event.statusId);
  }

  private confirmUnplan(data: PlanningItemData): void {
    this.confirmation
      .warn('Doc::Planning:UnplanConfirmMessage', 'Doc::Planning:UnplanConfirmTitle')
      .subscribeOnce(status => {
        if (status === Confirmation.Status.confirm) {
          this.unplanItem(data);
        }
      });
  }

  /** Right-click entries on item cards: open the detail (new tab) and unplan (back to the backlog). */
  protected readonly itemMenuItems: KanbanMenuItem<KanbanItem>[] = [
    {
      label: this.localization.instant('Doc::Planning:OpenDetail'),
      icon: 'digi-out-eye',
      action: item => this.onItemOpen(item),
    },
    {
      label: this.localization.instant('Doc::Planning:Unplan'),
      icon: 'digi-out-arrow-left',
      disabled: item => !(item.data as PlanningItemData).dto.plannedDate,
      disabledHint: this.localization.instant('Doc::Planning:UnplanDisabledHint'),
      action: item => this.unplanItem(item.data as PlanningItemData),
    },
  ];

  /** Right-click entries on group cards: rename the free-label group through the shared dialog. */
  protected readonly groupMenuItems: KanbanMenuItem<KanbanGroup>[] = [
    {
      label: this.localization.instant('::Edit'),
      icon: 'digi-out-edit-square',
      action: group => this.openRenameGroup(group.data as PlanningGroupData),
    },
  ];

  // ── Group / create-group dialogs ───────────────────────────────────────────────

  protected openRenameGroup(data: PlanningGroupData): void {
    const modalRef = this.modalService.open(PlanningGroupDialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      injector: this.injector,
    });
    modalRef.componentInstance.titleKey = 'Doc::Planning:EditGroup';
    modalRef.componentInstance.label = data.dto.label;
    modalRef.closed.subscribe((label: string) => {
      const trimmed = label?.trim();
      if (trimmed && trimmed !== data.dto.label) {
        this.onGroupRename(data, trimmed);
      }
    });
  }

  protected onCreateGroup(event: KanbanCreateGroupEvent): void {
    const bucket = this.bucketById(event.columnId);
    if (!bucket) {
      return;
    }
    const itemData = event.item ? (event.item.data as PlanningItemData) : undefined;
    const dataSource = itemData?.dataSource ?? this.groupingDataSource();
    if (!dataSource) {
      return;
    }
    const modalRef = this.modalService.open(PlanningGroupDialogComponent, {
      ariaLabelledBy: 'modal-basic-title',
      centered: true,
      injector: this.injector,
    });
    modalRef.closed.subscribe((label: string) => {
      const trimmed = label?.trim();
      if (trimmed) {
        this.createGroup(dataSource, bucket, trimmed, itemData);
      }
    });
  }

  // ── Mutations (optimistic, reload the affected source on error) ─────────────

  private planItem(
    data: PlanningItemData,
    bucket: PlanningScaleBucket,
    group: PlanningGroupDto | null,
    order: number,
  ): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    const date = toIsoDay(bucket.start);
    const board = this.boardData()[ds] ?? EMPTY_BOARD;
    const siblings = group
      ? board.planned.filter(item => item.groupId === group.id && item.id !== data.dto.id)
      : this.topLevelSlots(board, bucket.id, data.dto.id);
    const optimistic: PlanningItemDto = {
      ...data.dto,
      plannedDate: date,
      groupId: group?.id,
      // Slot it just before its target neighbour so it renders in its final position immediately;
      // the server re-sequences the whole sequence to integers, reconciled by the reload below.
      order: this.optimisticSlotOrder(siblings, order),
    };
    this.patchSource(ds, current => ({
      ...current,
      backlog: current.backlog.filter(item => item.id !== optimistic.id),
      planned: [...current.planned.filter(item => item.id !== optimistic.id), optimistic],
    }));
    // Reload rather than replace the single item: the server re-sequences the whole destination
    // sequence (groups + ungrouped items share one order per column), so the siblings' orders shift
    // too — a single-item replace would leave them stale. The optimistic fractional order already
    // shows the final position, so the reload reconciles to integers without a visible jump.
    adapter
      .plan({ configId: this.configId(), itemId: optimistic.id!, date, groupId: group?.id, order })
      .subscribeOnce(
        () => this.reloadSource(ds),
        () => this.reloadSource(ds),
      );
  }

  private unplanItem(data: PlanningItemData): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    // Unplanning resets the status to NotPlanned (the backend does the same), so reflect that immediately —
    // otherwise the card keeps showing its previous status in the backlog until the next full load.
    const optimistic: PlanningItemDto = {
      ...data.dto,
      plannedDate: undefined,
      groupId: undefined,
      statusId: WORK_ORDER_NOT_PLANNED_STATUS_ID,
    };
    this.patchSource(ds, board => ({
      ...board,
      planned: board.planned.filter(item => item.id !== optimistic.id),
      backlog: [optimistic, ...board.backlog.filter(item => item.id !== optimistic.id)],
    }));
    adapter
      .unplan({ configId: this.configId(), itemId: optimistic.id! })
      .subscribeOnce(undefined, () => this.reloadSource(ds));
  }

  /**
   * Manual status change from a card's status tag. Status doesn't affect placement, so this optimistically
   * swaps the item's statusId in place (updating the pill + the left border) and replaces it with the
   * server's dto on success; a failure reloads the source so the board reflects the true state.
   */
  private changeItemStatus(data: PlanningItemData, statusId: string): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    this.replaceItem(ds, { ...data.dto, statusId });
    adapter
      .changeStatus({ configId: this.configId(), itemId: data.dto.id!, statusId })
      .subscribeOnce(
        updated => this.replaceItem(ds, updated),
        () => this.reloadSource(ds),
      );
  }

  private moveGroup(data: PlanningGroupData, bucket: PlanningScaleBucket, index: number): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    const date = toIsoDay(bucket.start);
    const board = this.boardData()[ds] ?? EMPTY_BOARD;
    const optimisticOrder = this.optimisticSlotOrder(
      this.topLevelSlots(board, bucket.id, data.dto.id),
      index,
    );
    // The group card carries its members with it — re-date them optimistically too.
    this.patchSource(ds, current => ({
      ...current,
      groups: current.groups.map(group =>
        group.id === data.dto.id ? { ...group, date, order: optimisticOrder } : group,
      ),
      planned: current.planned.map(item =>
        item.groupId === data.dto.id ? { ...item, plannedDate: date } : item,
      ),
    }));
    // Reload rather than replace the single group: moving it re-sequences the whole column's shared
    // top-level order (groups + ungrouped items), so the siblings shift too and a single-group
    // replace would leave them stale and the reordered group would snap back into place.
    adapter
      .updateGroup({ configId: this.configId(), id: data.dto.id!, date, order: index })
      .subscribeOnce(
        () => this.reloadSource(ds),
        () => this.reloadSource(ds),
      );
  }

  private createGroup(
    dataSource: PlanningDataSource,
    bucket: PlanningScaleBucket,
    label: string,
    itemData?: PlanningItemData,
  ): void {
    const adapter = this.sourceMap().get(dataSource)?.adapter;
    if (!adapter) {
      return;
    }
    adapter
      .createGroup({ configId: this.configId(), date: toIsoDay(bucket.start), label })
      .subscribeOnce(group => {
        this.patchSource(dataSource, board => ({ ...board, groups: [...board.groups, group] }));
        if (itemData) {
          this.planItem(itemData, bucket, group, 0);
        }
      });
  }

  private deleteGroup(data: PlanningGroupData): void {
    const ds = data.dataSource;
    const adapter = this.sourceMap().get(ds)?.adapter;
    if (!adapter) {
      return;
    }
    this.patchSource(ds, board => ({
      ...board,
      groups: board.groups.filter(group => group.id !== data.dto.id),
      planned: board.planned.map(item =>
        item.groupId === data.dto.id ? { ...item, groupId: undefined } : item,
      ),
    }));
    adapter
      .deleteGroup({ configId: this.configId(), id: data.dto.id! })
      .subscribeOnce(undefined, () => this.reloadSource(ds));
  }

  // ── Data loading ──────────────────────────────────────────────────────────────

  private onConfigLoaded(config: PlanningConfigurationDto): void {
    this.config.set(config);
    // Show the planning's name as the browser tab title. The global title strategy resets it to the
    // app title on each navigation (including a configId change), so re-apply it once the config lands.
    this.title.setTitle(config.name?.current || config.code || this.title.getTitle());
    this.boardData.set({});
    this.backlogLimit.set(BACKLOG_PAGE_SIZE);
    this.filterPredicates.set({});
    this.visibilityStore.init(config.id);
    this.applyStoredViewState(config);
    this.resolveSources(config);
  }

  /**
   * Restores this board's persisted view state (zoom / displayed date / quick-search / weekend
   * toggle), falling back to defaults when absent or no longer valid (e.g. a saved zoom the config
   * no longer offers). The anchor is parsed from its ISO day into a LOCAL date so it matches the
   * scale engine's local-time bucketing.
   */
  private applyStoredViewState(config: PlanningConfigurationDto): void {
    const saved = this.viewStore.init(config.id);
    const ranges = config.ranges ?? [];
    this.range.set(
      saved.range && ranges.includes(saved.range) ? saved.range : this.coarsestRange(config),
    );
    if (saved.anchor) {
      const [year, month, day] = saved.anchor.split('-').map(Number);
      this.anchor.set(new Date(year, (month ?? 1) - 1, day ?? 1));
    } else {
      this.anchor.set(new Date());
    }
    this.search.set(saved.search ?? '');
    this.showWeekends.set(saved.showWeekends ?? true);
    this.fitToScreen.set(saved.fitToScreen ?? false);
    const filters = saved.filters ?? {};
    // `initialFilterState` seeds the controls (stable ref, set once); `filterStateByDs` tracks live
    // edits for persistence. Kept separate so a live edit doesn't feed back into the controls' seed.
    this.initialFilterState.set(filters);
    this.filterStateByDs.set(filters);
  }

  // ── Card field configuration (per user, per board) ──────────────────────────

  /** Resolved sources that expose a toggleable card-field catalogue, shaped for the config panel. */
  protected readonly configurableSources = computed<PlanningCardConfigSource[]>(() =>
    this.resolvedSources()
      .filter(source => source.adapter.cardFields?.length)
      .map(source => ({
        dataSource: source.dataSource,
        label: this.dataSourceLabel(source.dataSource),
        cardFields: source.adapter.cardFields!,
      })),
  );

  protected openCardConfig(panel: TemplateRef<unknown>): void {
    this.offcanvas.open(panel, null, {
      offcanvasPanelClass: OffcanvasSize.Small,
      position: 'end',
    });
  }

  // ── Board filters (per-source, client-side, instant) ────────────────────────

  /** Resolved sources that expose an interactive board filter component. */
  protected readonly filterSources = computed<ResolvedSource[]>(() =>
    this.resolvedSources().filter(source => source.adapter.boardFilterComponent),
  );

  protected dataSourceLabel(dataSource: PlanningDataSource): string {
    return (
      this.store.planningDataSources().find(entity => entity.id === dataSource)?.name?.current ??
      dataSource
    );
  }

  // Referentially-stable per-source maps: bound to the filter-host directive's inputs, so they must
  // NOT return a fresh reference every change-detection pass (that would refire the host's effect and
  // trip NG0103). They only change when the config / board data actually change.
  private readonly filterSettingsBySource = computed<Record<string, FilterSettingsMap>>(() => {
    const result: Record<string, FilterSettingsMap> = {};
    for (const source of this.config()?.dataSources ?? []) {
      if (source.dataSource) {
        result[source.dataSource] = source.filterSettings ?? EMPTY_FILTER_SETTINGS;
      }
    }
    return result;
  });

  private readonly itemsBySource = computed<Record<string, PlanningItemDto[]>>(() => {
    const data = this.boardData();
    const result: Record<string, PlanningItemDto[]> = {};
    for (const source of this.resolvedSources()) {
      const board = data[source.dataSource];
      result[source.dataSource] = board ? [...board.planned, ...board.backlog] : EMPTY_ITEMS;
    }
    return result;
  });

  protected filterSettingsFor(dataSource: PlanningDataSource): FilterSettingsMap {
    return this.filterSettingsBySource()[dataSource] ?? EMPTY_FILTER_SETTINGS;
  }

  protected itemsFor(dataSource: PlanningDataSource): PlanningItemDto[] {
    return this.itemsBySource()[dataSource] ?? EMPTY_ITEMS;
  }

  protected onFilterChange(
    dataSource: PlanningDataSource,
    predicate: (dto: PlanningItemDto) => boolean,
  ): void {
    this.filterPredicates.update(all => ({ ...all, [dataSource]: predicate }));
  }

  /** Restored filter selection used to seed a source's filter controls (undefined when none saved). */
  protected initialFilterStateFor(
    dataSource: PlanningDataSource,
  ): PlanningBoardFilterState | undefined {
    return this.initialFilterState()[dataSource];
  }

  /** Records a source's live filter selection so it is persisted with the rest of the view state. */
  protected onFilterStateChange(
    dataSource: PlanningDataSource,
    state: PlanningBoardFilterState,
  ): void {
    this.filterStateByDs.update(all => ({ ...all, [dataSource]: state }));
  }

  private resolveSources(config: PlanningConfigurationDto): void {
    const resolved: ResolvedSource[] = [];
    const unavailable: PlanningDataSource[] = [];
    for (const dataSourceDto of config.dataSources ?? []) {
      const dataSource = dataSourceDto.dataSource;
      if (!dataSource) {
        continue;
      }
      const adapterType = PLANNING_ADAPTERS[dataSource];
      if (adapterType) {
        resolved.push({ dataSource, adapter: this.injector.get(adapterType) });
      } else {
        unavailable.push(dataSource);
      }
    }
    this.unavailable.set(unavailable);
    this.resolvedSources.set(resolved);
    this.loadGroupingModes(config.id, resolved);
  }

  private loadGroupingModes(configId: string, sources: ResolvedSource[]): void {
    sources.forEach(source =>
      source.adapter
        .getBoardMeta(configId)
        .subscribeOnce(meta =>
          this.groupingByDs.update(all => ({ ...all, [source.dataSource]: meta.grouping })),
        ),
    );
  }

  /**
   * Board + backlog fetch for one source in the given window, mapped to a patch payload. Kept as a cold
   * observable (no internal subscribe) so the navigation pipeline can switchMap over it — cancelling the
   * previous window's requests — while the imperative {@link reloadSource} still drives it after a drop.
   */
  private loadSource(
    dataSource: PlanningDataSource,
    query: PlanningPeriodQuery,
  ): Observable<{ dataSource: PlanningDataSource; board: SourceBoard }> {
    const source = this.sourceMap().get(dataSource);
    if (!source) {
      return EMPTY;
    }
    return forkJoin({
      board: source.adapter.loadPlanned(query),
      backlog: source.adapter.loadBacklog(query, this.backlogLimit()),
    }).pipe(
      map(({ board, backlog }) => ({
        dataSource,
        board: {
          planned: board.items,
          backlog: backlog.items,
          backlogTotalCount: backlog.totalCount,
          groups: board.groups,
        },
      })),
    );
  }

  /** Imperative single-source reload used after a mutation lands (drop/create/rename/delete). */
  private reloadSource(dataSource: PlanningDataSource): void {
    const query = this.query();
    if (!query) {
      return;
    }
    this.loadSource(dataSource, query).subscribeOnce(({ board }) =>
      this.patchSource(dataSource, () => board),
    );
  }

  private readonly query = computed<PlanningPeriodQuery | null>(() => {
    const range = this.range();
    const config = this.config();
    // Guard the transient window after the routed configId changes but before its config has loaded:
    // this component is reused across planning routes, so configId is already the new id while config
    // (and the resolved data sources) still belong to the previous planning. Firing a load here would
    // ask the new configuration for the old configuration's data source and the backend rejects it
    // ("data source not part of this planning configuration"). Wait until the loaded config matches.
    if (!config || config.id !== this.configId() || !range) {
      return null;
    }
    return { configId: this.configId(), periodStart: this.anchor(), range };
  });

  // ── State helpers ─────────────────────────────────────────────────────────────

  private patchSource(
    dataSource: PlanningDataSource,
    patch: (board: SourceBoard) => SourceBoard,
  ): void {
    this.boardData.update(all => ({ ...all, [dataSource]: patch(all[dataSource] ?? EMPTY_BOARD) }));
  }

  private replaceGroup(dataSource: PlanningDataSource, dto: PlanningGroupDto): void {
    this.patchSource(dataSource, board => ({
      ...board,
      groups: board.groups.map(group => (group.id === dto.id ? dto : group)),
    }));
  }

  private replaceItem(dataSource: PlanningDataSource, dto: PlanningItemDto): void {
    this.patchSource(dataSource, board => ({
      ...board,
      planned: board.planned.map(item => (item.id === dto.id ? dto : item)),
      backlog: board.backlog.map(item => (item.id === dto.id ? dto : item)),
    }));
  }

  private bucketById(id: string): PlanningScaleBucket | undefined {
    return this.buckets().find(bucket => bucket.id === id);
  }

  private groupByKanbanId(kanbanGroupId: string): PlanningGroupDto | null {
    const dataSource = dataSourceOfKanbanId(kanbanGroupId);
    const groups = this.boardData()[dataSource]?.groups ?? [];
    return groups.find(group => groupKanbanId(dataSource, group.id!) === kanbanGroupId) ?? null;
  }

  private groupingDataSource(): PlanningDataSource | undefined {
    const grouping = this.groupingByDs();
    return this.resolvedSources().find(
      source => grouping[source.dataSource] !== PlanningGrouping.Denied,
    )?.dataSource;
  }

  private coarsestRange(config: PlanningConfigurationDto): PlanningPeriod | undefined {
    return [...(config.ranges ?? [])].sort((a, b) => (this.rank[a] ?? 0) - (this.rank[b] ?? 0))[0];
  }

  private bucketTitle(granularity: PlanningPeriod, bucket: PlanningScaleBucket): string {
    if (granularity === PlanningPeriod.Week) {
      return `W${this.datePipe.transform(bucket.start, 'w')}`;
    }
    if (granularity === PlanningPeriod.Month) {
      return this.datePipe.transform(bucket.start, 'MMM') ?? bucket.id;
    }
    return this.datePipe.transform(bucket.start, 'EEE') ?? bucket.id;
  }

  private bucketSubtitle(granularity: PlanningPeriod, bucket: PlanningScaleBucket): string {
    if (granularity === PlanningPeriod.Month) {
      return this.datePipe.transform(bucket.start, 'yyyy') ?? '';
    }
    return this.datePipe.transform(bucket.start, 'd MMM') ?? '';
  }
}
